import { useT } from '@/core/I18nProvider';

import { BEAT, enter } from './branch-beats';
import { CardTitle, Spacer } from './branch-parts';

import type { Branch } from './story-data';
import type { ReactElement } from 'react';

/**
 * Branch bodies 4–6: the chart, the cited answer, and the shipment tracker.
 *
 * Split from bodies 1–3 purely on the ~300-line ceiling; the file boundary
 * carries no meaning beyond that, which is why both halves import their shared
 * vocabulary from `branch-parts` rather than one importing the other.
 */

/* ------------------------------------------------------------------ */
/* 4 · Chart — a bar series built on the spot                          */
/* ------------------------------------------------------------------ */

const BARS = [32, 46, 41, 58, 67, 84, 100];

export function ChartBody({ branch }: { readonly branch: Branch }): ReactElement {
  const { t } = useT();
  const { at } = branch;
  return (
    <>
      <CardTitle at={at}>{t('home.story.chart.title')}</CardTitle>
      <div className="mt-3.5 flex flex-1 items-end gap-[7px] border-b border-[#e7eaef] pb-[7px]">
        {BARS.map((height, i) => (
          <div
            key={`${i}-${height}`}
            className="flex-1 origin-bottom rounded-t-[3px]"
            style={{
              height: `${height}%`,
              background: 'linear-gradient(180deg,var(--bd-cyan),var(--bd-blue))',
              animation: `sxBar .65s cubic-bezier(.34,1.56,.64,1) ${(at + BEAT.body + 0.9 + i * 0.06).toFixed(2)}s backwards`,
            }}
          />
        ))}
      </div>
      <div
        className="mt-2.5 flex flex-none items-baseline gap-2"
        style={{ animation: `sxUpS .45s ease ${(at + 3.9).toFixed(2)}s backwards` }}
      >
        <span className="text-[22px] font-semibold leading-none tracking-[-0.04em] text-[#0b0f1a]">
          $1.24m
        </span>
        <span className="text-[11.5px] text-[#5f6673]">{t('home.story.chart.delta')}</span>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 5 · Answer — a cited docs answer with a follow-up                   */
/* ------------------------------------------------------------------ */

export function AnswerBody({ branch }: { readonly branch: Branch }): ReactElement {
  const { t } = useT();
  const { at } = branch;
  return (
    <>
      <div className="mt-3 flex flex-none flex-col gap-2">
        {['s1', 's2', 's3'].map((step, i) => (
          <div
            key={step}
            className="flex gap-2.5 text-[12.5px] leading-snug text-[#111827]"
            style={{
              animation: `sxInL .4s cubic-bezier(.16,1,.3,1) ${(at + BEAT.body + i * 0.18).toFixed(2)}s backwards`,
            }}
          >
            <span className="mt-0.5 flex-none font-mono text-[9.5px] text-[#0f766e]">0{i + 1}</span>
            <span>{t(`home.story.answer.${step}`)}</span>
          </div>
        ))}
      </div>
      <Spacer />
      <div
        className="flex flex-none flex-wrap gap-1.5"
        style={{ animation: `sxUpS .4s ease ${(at + 3.4).toFixed(2)}s backwards` }}
      >
        {['docs/editor', 'docs/shortcuts'].map((source) => (
          <span
            key={source}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#ececef] bg-[#f6f6f8] px-2 py-1 font-mono text-[9.5px] text-[#5f6673]"
          >
            <span aria-hidden="true" className="size-1 rounded-full bg-[color:var(--bd-violet)]" />
            {source}
          </span>
        ))}
      </div>
      <div
        className="mt-2 flex flex-none items-center gap-2 rounded-[10px] border border-[#e7eaef] bg-white px-2.5 py-2"
        style={{
          animation: `sxUp .45s cubic-bezier(.34,1.56,.64,1) ${(at + 3.7).toFixed(2)}s backwards`,
        }}
      >
        <span className="flex-none font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.07em] text-[#0f766e] rtl:normal-case rtl:tracking-normal">
          {t('home.story.answer.next')}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12px] text-[#111827]">
          {t('home.story.answer.nextAsk')}
        </span>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 6 · Shipment — a live tracker                                       */
/* ------------------------------------------------------------------ */

const STEPS = ['placed', 'packed', 'shipped', 'delivery'];

export function ShipmentBody({ branch }: { readonly branch: Branch }): ReactElement {
  const { t } = useT();
  const { at } = branch;
  return (
    <>
      <CardTitle at={at}>{t('home.story.shipment.title')}</CardTitle>
      <div
        className="mt-3 flex flex-none items-center gap-2.5 rounded-xl border border-[#e7eaef] bg-white p-2"
        style={{ animation: enter(at, BEAT.body) }}
      >
        <span
          className="size-9 flex-none rounded-lg"
          style={{ background: 'linear-gradient(140deg,var(--bd-blue),var(--bd-purple))' }}
        />
        <span className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="truncate text-[12.5px] font-semibold text-[#0b0f1a]">RTX 4080</span>
          <span className="truncate text-[11px] text-[#5f6673]">
            {t('home.story.shipment.carrier')}
          </span>
        </span>
        <span className="flex-none font-mono text-[10px] text-[#6e6e77]">#48213</span>
      </div>

      <div className="mt-4 flex flex-none items-center">
        {STEPS.map((step, i) => (
          <div key={step} className="flex flex-1 items-center last:flex-none">
            <span className="flex flex-col items-center gap-1.5">
              <span
                aria-hidden="true"
                className={[
                  'grid size-[18px] place-items-center rounded-full text-[9px]',
                  i <= 2 ? 'bg-[#047857] text-white' : 'border-2 border-[#e7eaef] bg-white',
                ].join(' ')}
                style={{
                  animation: `sxPopS .4s cubic-bezier(.34,1.56,.64,1) ${(at + BEAT.body + 0.5 + i * 0.2).toFixed(2)}s backwards`,
                }}
              >
                {i <= 2 ? '✓' : ''}
              </span>
              <span className="whitespace-nowrap text-[9.5px] text-[#5f6673]">
                {t(`home.story.shipment.${step}`)}
              </span>
            </span>
            {i < STEPS.length - 1 ? (
              <span
                aria-hidden="true"
                className="mx-1 -mt-5 h-0.5 flex-1 origin-[left_center] rounded-sm rtl:origin-[right_center]"
                style={{
                  background: i <= 1 ? '#047857' : '#e7eaef',
                  animation: `sxGrowX .4s ease ${(at + BEAT.body + 0.6 + i * 0.2).toFixed(2)}s backwards`,
                }}
              />
            ) : null}
          </div>
        ))}
      </div>

      <Spacer />
      <div
        className="flex flex-none items-baseline gap-2 rounded-[10px] border border-[rgba(245,158,11,.18)] bg-[rgba(245,158,11,.06)] px-2.5 py-2"
        style={{ animation: `sxUpS .45s ease ${(at + 3.6).toFixed(2)}s backwards` }}
      >
        <span className="text-[11.5px] text-[#7a5206]">{t('home.story.shipment.arriving')}</span>
        <span className="ms-auto whitespace-nowrap text-[12.5px] font-semibold text-[#7a5206]">
          {t('home.story.shipment.eta')}
        </span>
      </div>
    </>
  );
}
